import React, { ReactElement } from "react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    useSensor,
    useSensors,
    UniqueIdentifier,
    TouchSensor,
    MouseSensor,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    SortingStrategy,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
    restrictToFirstScrollableAncestor,
    restrictToParentElement,
} from "@dnd-kit/modifiers";
import { useSortableDragEventHandlers } from "organisms/sortable-drap-and-drop/use-sortable-drag-event-handlers";

/*
Implementation:

    const SomeComponent = () => {
        const [items, setItems] = useState<SomeItemRecord[]>();

        const handleOnSortChange = (orderedItems: SomeItem[]) => {
            const itemsAsRecords = orderedItems.map((item) =>
                new SomeItemRecord().with({ ...item })
            );
            // Update frontend state, or persist to DB via an API call
            setItems([...itemsAsRecords]);
        };

        return (
            <SortableDragAndDropList items={items} onSortChange={handleOnSortChange}>
                {items.map((item: SomeItemRecord) => (
                    <DragAndDropItem id={item.id!} key={item.id!}>
                        <SomeNiceLookingComponent item={item} key={item.id} />
                    </DragAndDropItem>
                ))}
            </SortableDragAndDropList>
        )
    }

Works with:
- Any child components
- Any record or interface as long as it has an id and some property to sort on

Pitfalls:
    - items should be sorted based on the property that is driving the resorting, or there may be unexpected results
    - item records must had a unique `id` property and some sort of property to sort on

*/

const DEFAULT_DISPLAY_ORDER_PROPERTY = "displaySequence";

export interface SortableDragAndDropListProps {
    items: { id?: any }[];
    onDragStart?: () => void;
    onSortChange: (any) => void;
    children: ReactElement<{ id: UniqueIdentifier }>[];
    sortProperty?: string;
    strategy?: SortingStrategy;
}

const SortableDragAndDropList = (props: SortableDragAndDropListProps) => {
    const {
        items,
        onSortChange,
        children,
        onDragStart = () => {},
        sortProperty = DEFAULT_DISPLAY_ORDER_PROPERTY,
        strategy = verticalListSortingStrategy,
    } = props;
    const { handleDragEnd } = useSortableDragEventHandlers(
        items,
        onSortChange,
        sortProperty
    );
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 10,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    return (
        <DndContext
            modifiers={[
                restrictToParentElement,
                restrictToFirstScrollableAncestor,
            ]}
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={handleDragEnd}>
            <SortableContext
                items={items as unknown as { id: UniqueIdentifier }[]}
                strategy={strategy}>
                {children}
            </SortableContext>
        </DndContext>
    );
};

export default SortableDragAndDropList;
